"use client";

import { useEffect, useState } from "react";

const GENERATION_STEPS = [
  "Reading your birth coordinates...",
  "Mapping your archetypal field...",
  "Identifying your primary archetype...",
  "Uncovering your core strengths...",
  "Revealing your growth edges...",
  "Tracing your life path themes...",
  "Composing your 30-Day Alignment Plan...",
  "Weaving your Avatar Identity...",
  "Finalizing your Blueprint...",
];

interface GeneratingAnimationProps {
  name: string;
}

export default function GeneratingAnimation({ name }: GeneratingAnimationProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        if (prev < GENERATION_STEPS.length - 1) return prev + 1;
        return prev;
      });
    }, 700);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 95) return prev + Math.random() * 4;
        return prev;
      });
    }, 250);

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <div className="w-full max-w-lg mx-auto text-center fade-in">
      {/* Animated Nautilus Spiral */}
      <div className="flex justify-center mb-10">
        <div className="relative w-32 h-32">
          {/* Outer ring */}
          <div
            className="absolute inset-0 rounded-full border-2 border-dashed"
            style={{
              borderColor: "rgba(219,184,77,0.3)",
              animation: "spin 12s linear infinite",
            }}
          />
          {/* Middle ring */}
          <div
            className="absolute inset-4 rounded-full border-2"
            style={{
              borderColor: "rgba(124,62,97,0.5)",
              animation: "spin 8s linear infinite reverse",
            }}
          />
          {/* Inner ring */}
          <div
            className="absolute inset-8 rounded-full border-2"
            style={{
              borderColor: "rgba(219,184,77,0.6)",
              animation: "spin 4s linear infinite",
            }}
          />
          {/* Core */}
          <div
            className="absolute inset-12 rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(219,184,77,0.8) 0%, rgba(124,62,97,0.6) 100%)",
              animation: "pulse 2s ease-in-out infinite",
              boxShadow: "0 0 20px rgba(219,184,77,0.5)",
            }}
          />
        </div>
      </div>

      <h2
        className="text-2xl md:text-3xl font-display mb-2 text-white/90"
        style={{ fontWeight: 700 }}
      >
        Crafting Your Avatar Blueprint & Life Purpose Report
      </h2>
      <p className="text-base text-white/50 mb-10" style={{ fontWeight: 400 }}>
        {name ? `Reading the patterns within your journey, ${name.split(" ")[0]}...` : "Reading the patterns within your journey..."}
      </p>

      {/* Progress Bar */}
      <div
        className="w-full h-2 rounded-full mb-4 overflow-hidden"
        style={{ background: "rgba(219,184,77,0.1)", border: "1px solid rgba(219,184,77,0.15)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-300 ease-out"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #7c3e61, #dbb84d)",
            boxShadow: "0 0 8px rgba(219,184,77,0.4)",
          }}
        />
      </div>
      <p className="text-xs text-gold-DEFAULT mb-8 font-mono" style={{ fontVariantNumeric: "tabular-nums" }}>
        {Math.round(progress)}% complete
      </p>

      {/* Step list */}
      <div className="space-y-2 text-left">
        {GENERATION_STEPS.map((step, index) => (
          <div
            key={index}
            className="flex items-center gap-3 transition-all duration-300"
            style={{
              opacity: index <= currentStep ? 1 : 0.2,
            }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
              style={{
                background: index < currentStep
                  ? "linear-gradient(135deg, #7c3e61, #dbb84d)"
                  : index === currentStep
                  ? "rgba(219,184,77,0.2)"
                  : "rgba(255,255,255,0.05)",
                border: index <= currentStep ? "none" : "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {index < currentStep ? (
                <svg className="w-3 h-3 text-deep-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : index === currentStep ? (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ background: "#dbb84d", animation: "pulse 1s ease-in-out infinite" }}
                />
              ) : null}
            </div>
            <span
              className="text-sm transition-all duration-300"
              style={{
                color: index === currentStep ? "#dbb84d" : index < currentStep ? "rgba(240,232,245,0.7)" : "rgba(240,232,245,0.2)",
                fontWeight: index === currentStep ? 600 : 400,
              }}
            >
              {step}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}